// Small pieces shared by the embed's modes.
import { LEVEL_COLORS, type Family } from "@/atlas/familyMode";
import type { AtlasScenario } from "@/atlas/data";
import type { makeT } from "./i18n";

type T = ReturnType<typeof makeT>;

export const GRADE_COLOR: Record<string, string> = { A: "#1f7a4d", B: "#4f8a2c", C: "#9a7a12", D: "#b4560f", F: "#a81f2e" };

export function GradeChip({ g, t }: { g: string | null; t: T }) {
  if (!g) return <span className="emb-grade none" aria-label={t("ungraded")} title={t("ungraded")}>–</span>;
  return <span className="emb-grade" style={{ background: GRADE_COLOR[g] }} aria-label={t("grade", { g })}>{g}</span>;
}

export function Stars({ n, t }: { n: number | null | undefined; t: T }) {
  if (!n) return <span className="muted">{t("unrated")}</span>;
  const r = Math.round(n);
  return <span aria-label={`${n} / 5`}><span aria-hidden="true">{"★".repeat(r)}{"☆".repeat(5 - r)}</span> {n}/5</span>;
}

/** The family's variants grouped by cost level; picking one switches what the
 *  frame prices. Same levels and colours as the scenario page and Atlas. */
export function VariantPicker({ id, family, current, onPick, nameOf, money, t }: {
  id: string; family: Family; current: string; onPick: (scenarioId: string) => void;
  nameOf: (s: AtlasScenario) => string; money: (n: number) => string; t: T;
}) {
  return (
    <div id={id} className="emb-picker">
      <p className="emb-sub">{t("levelHint")}</p>
      {family.byLevel.map((rows, level) => rows.length === 0 ? null : (
        <div key={level} className="emb-level">
          <div className="emb-level-head">
            <i style={{ background: LEVEL_COLORS[level] }} aria-hidden="true" />
            <b>{t("levels").split(", ")[level]}</b><span className="src">· {rows.length}</span>
          </div>
          <ul>
            {rows.map((m) => (
              <li key={m.s.id}>
                <button type="button" aria-pressed={m.s.id === current} className={m.s.id === current ? "on" : ""} onClick={() => onPick(m.s.id)}>
                  <span className="n">{nameOf(m.s)}</span>
                  {m.s.nc != null && <span className="src">{money(m.s.nc)}</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
